import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/Logo";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { Toaster, toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Något gick fel. Försök igen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ username: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (user && user.username) {
      const from = location.state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.error("Fyll i användarnamn och lösenord.");
      return;
    }
    setSubmitting(true);
    try {
      await login(form.username.trim(), form.password);
      toast.success("Inloggad!");
      const from = location.state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-login-page">
      <Toaster position="top-center" richColors />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} /> Tillbaka till startsidan
          </Link>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <Logo size={40} />
              <div className="leading-tight">
                <div className="font-extrabold text-slate-900 text-base">
                  KJ Grönt &amp; Rent
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-green-700 font-semibold">
                  Admin · Vänge
                </div>
              </div>
            </div>
            <h1 className="mt-6 text-2xl font-extrabold text-slate-900">
              Logga in
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Endast för administratör.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="login-form">
              <label className="block">
                <span className="block text-sm font-medium text-slate-900 mb-1.5">
                  Användarnamn
                </span>
                <input
                  type="text"
                  autoComplete="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  data-testid="login-username"
                  className="block w-full rounded-xl border-0 py-3 px-4 text-slate-900 bg-white shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm"
                  placeholder="kj"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-900 mb-1.5">
                  Lösenord
                </span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="login-password"
                  className="block w-full rounded-xl border-0 py-3 px-4 text-slate-900 bg-white shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm"
                  placeholder="••••••••"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-5 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Loggar in...
                  </>
                ) : (
                  <>
                    <Lock size={16} /> Logga in
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
